import * as React from 'react';
import { useState } from 'react';
import { Box, Typography, Grid, Chip, Stack, Button } from '@mui/material';
import { useTheme } from '@emotion/react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Apiconfig from '../../../config/ApiConfig';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import jwtDecode from 'jwt-decode';
import Pagination from '@mui/material/Pagination';
import { ToastContainer } from 'react-toastify';
import AddMachineDialogTest from '../GridView/Component/AddMachineDialogTest';

const handleTotalSection = (section) => {
    let count = 0;
    for (const i of section) {
        count += i.lectures.length;
    }
    return count;
};

const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return '';
    const cleanPath = thumbnail.replace(/\\/g, '/');
    return cleanPath.startsWith('/')
        ? `${Apiconfig.url}${cleanPath}`
        : `${Apiconfig.url}/${cleanPath}`;
};

const ListView = ({
    data,
    myCourse,
    page,
    setPage,
    count,
    getConversations,
    myCourses,
}) => {
    const [courseId, setCourseId] = useState('');
    const [addMachineDialogState, setDialogState] = useState(false);

    const theme = useTheme();
    const location = useLocation();

    const handleChange = (e, p) => {
        setPage(p);
        // Correct ternary execution without standalone block braces
        myCourse ? myCourses(p) : getConversations(p);
    };

    const addMachine = (id) => {
        setCourseId(id);
        setDialogState(true);
    };

    const closeMachineDialog = () => setDialogState(false);

    const token = localStorage.getItem('token');
    const user = token ? jwtDecode(token) : null;

    return (
        <>
            {data?.map((course) => (
                <Grid
                    container
                    spacing={2}
                    key={course._id || course.id}
                    alignItems="center"
                    mb={7}
                    sx={{
                        py: 2,
                        position: 'relative',
                        '&:before': {
                            position: 'absolute',
                            bottom: 0,
                            backgroundColor: 'rgb(25 49 79 / 20%)',
                            content: '""',
                            width: '100%',
                            height: '0.2px',
                            filter: 'blur(0.8px)',
                        },
                    }}
                >
                    <Grid item md={2}>
                        <img
                            src={getThumbnailUrl(course.thumbnail)}
                            style={{ borderRadius: '5px' }}
                            alt="myCourse"
                            width="202px"
                            height="126px"
                        />
                    </Grid>
                    <Grid
                        item
                        sm={12}
                        md={5}
                        lg={7}
                        sx={{
                            display: 'block',
                            justifyContent: 'space-between',
                            alignItems: 'end',
                        }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1}>
                                <Chip label={course.majorCategory} />
                            </Stack>
                            <Link
                                to={{
                                    pathname: `/dashboard/courses/view-course/${course._id}`,
                                }}
                                state={
                                    myCourse
                                        ? { myCourse: 'myCourse' }
                                        : { myCourse: 'allCourse' }
                                }
                            >
                                <Typography
                                    color="text.primary"
                                    variant="h4"
                                    sx={{
                                        mt: 1,
                                        minWidth: '300px',
                                    }}
                                >
                                    {course.courseName.charAt(0).toUpperCase() +
                                        course?.courseName.slice(1)}
                                </Typography>
                            </Link>
                        </Box>
                        <Box display="flex">
                            <Typography variant="body2">
                                {course.sections.length} Module(s)
                            </Typography>
                            <Typography variant="body2" sx={{ mx: 2 }}>
                                {handleTotalSection(course.sections)} Lessons
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={5} lg={3}>
                        <Box
                            display="flex"
                            alignItems="end"
                            flexDirection="column"
                            gap={1}
                            pt={2}
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    gap: '20px',
                                    justifyContent: 'space-between',
                                },
                            }}
                        >
                            <Link
                                to={{
                                    pathname:
                                        '/dashboard/courses/track-progress',
                                }}
                                state={{
                                    course: course,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '17px',
                                        fontWeight: '400',
                                        color: 'primary.light',
                                    }}
                                >
                                    <DonutLargeIcon
                                        sx={{
                                            fontSize: '17px',
                                            marginBottom: '-2px',
                                            marginRight: '8px',
                                        }}
                                    />
                                    Track Progress
                                </Typography>
                            </Link>
                            <Grid
                                item
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                            >
                                {user?.role === 'admin' &&
                                    location?.pathname ===
                                        '/dashboard/courses' && (
                                        <Link
                                            to={{
                                                pathname: `/dashboard/courses/edit-course/${course._id}`,
                                            }}
                                            state={{
                                                isEdit: true,
                                                courseId: course._id,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                color="view"
                                                variant="outlined"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Edit
                                            </Button>
                                        </Link>
                                    )}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            ))}

            <AddMachineDialogTest
                open={addMachineDialogState}
                setOpen={addMachine}
                handleClose={closeMachineDialog}
                courseId={courseId}
            />
            <ToastContainer />

            <Box display="flex" justifyContent="center">
                <Pagination
                    color="secondary"
                    variant="outlined"
                    count={count}
                    page={page}
                    onChange={handleChange}
                />
            </Box>
        </>
    );
};

ListView.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    myCourse: PropTypes.bool.isRequired,
    page: PropTypes.number.isRequired,
    setPage: PropTypes.func.isRequired,
    count: PropTypes.number.isRequired,
    getConversations: PropTypes.func.isRequired,
    myCourses: PropTypes.func.isRequired,
};

export default ListView;
